import SearchSelect from "@ui-components/SearchSelect";
import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {COMPONENT_STYLE_OVERRIDE} from "./lib/consts";
import {ExtendedTableFilterProps, Option, NullFilterValue} from "./types";
import {NullFilter} from "./null-filter";
import _ from "lodash";
import {ArrowDownOnSquareStackIcon, NoSymbolIcon} from "@heroicons/react/24/outline";


const ALL_SELECTOR_TOKEN = "__SELECT_ALL";


export const ExtendedTableFilter = <T extends object>({
  fetch,
  loading,
  columnDef,
  onFilterChanged,
  hasSearch,
  hasNullFlag,
  allowSelectAll = true,
  limit = 20,
  isAsync = true,
  ...rest
}: ExtendedTableFilterProps<T>) => {

  if (!hasSearch && !hasNullFlag)
    throw new Error("You must specify at least one of search or null flag capabilities");

  // hooks
  const intl = useIntl();

  // state
  const [options, setOptions] = useState<Option[]>([]);
  const [selected, setSelected] = useState<Option[]>([]);
  const [nullFlagValue, setNullFlagValue]
    = useState<NullFilterValue>("all");
  
  // utilities
  const processOptions = (options: Option[], text: string) => {
    if (allowSelectAll && options.length > 1) {
      // If there's a number of options between 1 and LIMIT, add an "ALL" selector; if the user clicks
      // on it, we will insert all the options that match the search
      const isAllOptionDisabled = options.length >= limit;
      const Icon = isAllOptionDisabled ? NoSymbolIcon : ArrowDownOnSquareStackIcon;
      options.unshift({
        value: `${ALL_SELECTOR_TOKEN}__${text}`,
        label: (
          <div className={`flex flex-col gap-1 ${isAllOptionDisabled ? 'text-gray-400' : 'text-kerakoll-700'}`}>
            <div className="flex flex-row items-center gap-1">
              <Icon className="w-4" />
              <FormattedMessage id="select_all"/>
            </div>
            {isAllOptionDisabled && (
              <p className="text-xs">
                <FormattedMessage
                  id="select_all_disabled_helper_title"
                  values={{limit}}
                />
              </p>
            )}
          </div>
        ),
        isDisabled: isAllOptionDisabled,
      });
    }
    return options;
  }

  useEffect(() => {
    // Update filter value when selected changes
    const value = {
      selected: hasSearch ? selected.map((o) => o.value) : undefined,
      nullFlag: hasNullFlag ? nullFlagValue : undefined,
    }
    onFilterChanged((columnDef as any).tableData.id, value);
  }, [selected, nullFlagValue]);

  useEffect(() => {
    // Fetch options when the component mounts (if it's not async)
    if (!isAsync) {
      fetch("", -1)
        ?.then((options) => setOptions(
          processOptions(
            options.filter((o) => !!o.value || parseInt(o.value) === 0),
            ""
          ))
        );
    }
  }, []);  //[fetch]);

  return (
    <div className="flex flex-col gap-2">
      {hasSearch && !!fetch && (
        <SearchSelect
          value={selected}
          options={isAsync ? undefined : options}
          loadOptions={
            isAsync
              ? _.debounce(
                (text: string, callback: (options: Option[]) => void) => {
                  fetch(text, limit)
                    ?.then((options) => processOptions(options, text))
                    .then(callback);
                },
                500
              )
              : undefined
          }
          onChange={async (selected: Option[]) => {
            const local = _.cloneDeep(selected);
            if (allowSelectAll) {
              const allOptionIdx = local.findIndex((o) => o.value.startsWith(ALL_SELECTOR_TOKEN));
              if (allOptionIdx >= 0) {
                // User asked to add all the results from their search --> Fetch them using the data function, then add
                // them to the selected list, and exclude the "ALL" selector
                const value = local[allOptionIdx].value
                  .replace(`${ALL_SELECTOR_TOKEN}__`, "");
                const options = await fetch(value, -1)
                  ?.then(
                    (o) => o.filter((o) => !local.some((l) => l.value === o.value))
                  )
                  ?? [];
                local.splice(allOptionIdx, 1);
                local.push(...options);
              }
            }
            setSelected(local);
          }}
          noOptionsMessage={() => <FormattedMessage id="type_to_search" />}
          isClearable={true}
          isLoading={loading}
          isMulti={true}
          // @ts-ignore
          inputStyle={{position: "relative"}}
          // @ts-ignore
          componentStyle={COMPONENT_STYLE_OVERRIDE}
          mainStyle="border-2 border-kerakoll-400 mt-1 mb-0"
          {...rest}
        />
      )}
      {hasNullFlag && (
        <NullFilter
          id={`${(columnDef as any).tableData.id}-null-filter`}
          value={nullFlagValue}
          onChange={setNullFlagValue}
        />
      )}
    </div>
  )
}
